import type {ScriptSnapshot} from './scriptPersistence';

export interface ScriptEditIntent { label: string; group?: string; focusId?: string }
interface Point { snapshot: ScriptSnapshot; size: number; label: string; focusId?: string }

/** Detach editable objects without repeatedly serializing the immutable source
 * XML. Undo history is transient; cloud revisions belong to ScriptSaveSession. */
function copy(snapshot: ScriptSnapshot): ScriptSnapshot {
    const source = snapshot.document?.sourceFDX;
    const result = JSON.parse(JSON.stringify({...snapshot, document: snapshot.document
        ? {...snapshot.document, sourceFDX: source ? {...source, xml: undefined} : undefined} : undefined})) as ScriptSnapshot;
    if (source) result.document!.sourceFDX!.xml = source.xml;
    return result;
}
function key(snapshot: ScriptSnapshot): string {
    // Include source identity/content in comparisons, but keep the large XML
    // outside the JSON used for the rest of the document.
    const source = snapshot.document?.sourceFDX;
    return JSON.stringify({...snapshot, document: snapshot.document
        ? {...snapshot.document, sourceFDX: source ? {...source, xml: undefined} : undefined} : undefined});
}
function same(a: ScriptSnapshot, b: ScriptSnapshot): boolean {
    return a.document?.sourceFDX?.xml === b.document?.sourceFDX?.xml && key(a) === key(b);
}
function point(snapshot: ScriptSnapshot, label: string, focusId?: string): Point {
    return {snapshot:copy(snapshot), size:key(snapshot).length * 2, label, focusId};
}

/** One timeline per loaded account/project, mirroring the existing immutable
 * editor snapshot. Commands stay separate; adjacent typing can form one step. */
export class ScriptUndoHistory {
    private past: Point[] = [];
    private future: Point[] = [];
    private present: Point;
    private group: string | undefined;
    private lastEdit = 0;
    constructor(initial: ScriptSnapshot, private options = {limit:100, maxBytes:32 * 1024 * 1024, groupMs:1000}) {
        this.present = point(initial, 'Initial document');
    }
    get canUndo() { return this.past.length > 0; }
    get canRedo() { return this.future.length > 0; }
    get undoLabel() { return this.past.at(-1)?.label; }
    get redoLabel() { return this.future.at(-1)?.label; }
    get snapshot() { return copy(this.present.snapshot); }
    endGroup() { this.group = undefined; }

    record(snapshot: ScriptSnapshot, intent: ScriptEditIntent = {label:'Edit screenplay'}, now = Date.now()): boolean {
        if (same(this.present.snapshot, snapshot)) return false;
        const grouped = intent.group !== undefined && intent.group === this.group
            && now >= this.lastEdit && now - this.lastEdit <= this.options.groupMs && this.future.length === 0;
        if (!grouped) this.past.push({...this.present, label:intent.label, focusId:intent.focusId});
        this.present = point(snapshot, intent.label, intent.focusId);
        this.future = [];
        this.group = intent.group; this.lastEdit = now;
        this.trim(); return true;
    }
    undo(): {snapshot:ScriptSnapshot; focusId?:string} | undefined {
        const previous = this.past.pop(); if (!previous) return;
        this.future.push({...this.present, label:previous.label});
        this.present = previous; this.endGroup();
        return {snapshot:this.snapshot, focusId:previous.focusId};
    }
    redo(): {snapshot:ScriptSnapshot; focusId?:string} | undefined {
        const next = this.future.pop(); if (!next) return;
        this.past.push({...this.present, label:next.label, focusId:next.focusId});
        this.present = next; this.endGroup();
        return {snapshot:this.snapshot, focusId:next.focusId};
    }
    private trim() {
        while (this.past.length > this.options.limit) this.past.shift();
        const bytes = () => {
            const all = [...this.past, this.present, ...this.future];
            const sources = new Set(all.map(item => item.snapshot.document?.sourceFDX?.xml).filter((xml): xml is string => xml !== undefined));
            return all.reduce((sum,item) => sum + item.size,0) + Array.from(sources).reduce((sum,xml) => sum + xml.length * 2,0);
        };
        while (this.past.length && bytes() > this.options.maxBytes) this.past.shift();
    }
}
